import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Newspaper } from "lucide-react";
import { api } from "@/lib/api";

export function HomeTestimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section data-testid="home-testimonials" className="relative py-20 sm:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,176,255,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Testimonials</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            WHAT FOUNDERS <span className="text-gradient-saffron">SAY.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-testimonials-grid">
          {items.slice(0, 6).map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
              className="card-beam glass rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1.5"
              data-testid={`testimonial-card-${t.id}`}
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, s) => (
                  <Star key={s} className="w-4 h-4 text-[#FF8800] fill-[#FF8800]" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">"{t.text}"</p>
              <div className="mt-6 flex items-center gap-3.5">
                {t.photo ? (
                  <img src={t.photo} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF8800]/30 to-[#00B0FF]/20 border border-white/10 flex items-center justify-center font-display font-bold text-white text-sm">
                    {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div>
                  <p className="font-display font-semibold text-white text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.designation}{t.company ? `, ${t.company}` : ""}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InsightsTeaser() {
  const [posts, setPosts] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 2))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 1))).catch(() => {});
  }, []);

  const items = [
    ...posts.map((p) => ({ kind: "Blog", title: p.title, sub: p.excerpt, to: `/blog/${p.slug}`, id: p.id })),
    ...news.map((n) => ({ kind: "News", title: n.headline, sub: n.summary, to: "/news", id: n.id })),
  ];
  if (items.length === 0) return null;

  return (
    <section data-testid="home-insights" className="relative py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-end justify-between gap-6 mb-10"
        >
          <div>
            <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Insights</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
              FROM THE <span className="text-gradient-saffron">ECOSYSTEM.</span>
            </h2>
          </div>
          <Link to="/blog" data-testid="insights-view-all" className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#FF8800] transition-colors shrink-0">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="insights-grid">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={item.to}
                data-testid={`insights-card-${item.kind.toLowerCase()}-${item.id}`}
                className="group block glass rounded-2xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8800]/40"
              >
                <span className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] text-[#00B0FF] uppercase">
                  <Newspaper className="w-3 h-3" /> {item.kind}
                </span>
                <p className="mt-3 font-display font-semibold text-white leading-snug group-hover:text-[#FF8800] transition-colors">{item.title}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{item.sub}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
